<script lang="ts">
  import { startOfDay } from "date-fns";
  import { onMount } from "svelte";
  import { api } from "../api/client";
  import { priceHistoryDatePickerState } from "../helpers/store";
  import {
    type Buyer,
    type DatabaseMatch,
    type InvoiceLine,
    type MassUploadInvoice,
    type Recipe,
    type RecipeFull,
    type RecipeLineMatch,
  } from "../helpers/types";
  import ButtonSmall from "./ButtonSmall.svelte";
  import LoadingSpinner from "./LoadingSpinner.svelte";
  import RecipeCostingPriceLine from "./RecipeCostingPriceLine.svelte";
  import RecipeIngredientLink from "./RecipeIngredientLink.svelte";
  import Text from "./Text.svelte";
  import DatePicker from "./date-picker/DatePicker.svelte";

  type $FixMe = any;

  export let buyer: Buyer | null;
  export let recipes: RecipeFull[];
  export let isOpen: boolean = false;
  export let searchIndex: Map<string, Recipe> = new Map();
  export let databaseConnectedLines: DatabaseMatch[] = [];
  export let percentIngredientsLinkedForRecipe: Record<string, number> = {};

  let currentStep = 0;
  let invoices: MassUploadInvoice[];
  let selectedRecipeIds: string[] = [];
  let connectedLines: RecipeLineMatch[] = [];
  let connectedLinesManual: RecipeLineMatch[] = [];
  let allInvoiceLines: InvoiceLine[] = [];
  let unallocatedInvoiceLines: InvoiceLine[] = [];
  let allocatedInvoiceLines: string[] = [];

  let recipeSold: Record<string, number> = {};
  let ingredientSoldQty: Record<string, number> = {};
  let recipeFlatIngredients: Record<string, RecipeFull>;
  let ingredientPriceOverrides: Record<string, number | null> = {};

  let linkingSalesData: boolean = false;
  let waitingForSalesDataPaste: boolean = false;

  let linkingRecipesToInvoices: boolean = false;
  let gettingInvoiceLines: boolean = false;

  let steps = [
    {
      title: "Sales",
      description:
        "Choose your sales period and enter the quantities of each dish sold to calculate your Gross Profit (GP) for that interval.",
      component: "recipes",
    },
    // {
    //   title: "Connect",
    //   description: "Connect recipes to invoice lines",
    //   component: "connect",
    // },
    {
      title: "Calculate",
      description: "Calculate recipe prices",
      component: "calculate",
    },
  ];

  let emailCopySuccess: boolean = false;

  function emailCopy(text: string) {
    navigator.clipboard.writeText(text);
    emailCopySuccess = true;
    setTimeout(() => {
      emailCopySuccess = false;
    }, 2000);
  }

  export function open() {
    currentStep = 0;
    isOpen = true;
  }

  function nextStep() {
    if (currentStep < steps.length - 1) {
      currentStep += 1;
    }
  }

  function ingredientPriceOverrideSet(ingredientId: string, price: number | null) {
    ingredientPriceOverrides[ingredientId] = price;
  }

  function collectIngredients(recipe: RecipeFull, mainRecipeId: string) {
    let flatRecipe: RecipeFull[] = [];
    if (recipe.items === undefined) return flatRecipe;

    for (const ingredient of recipe.items) {
      if (!ingredient.id) continue;
      if (ingredient.type === "recipe") {
        recipeSold[ingredient.id] = recipeSold[mainRecipeId] * (recipe.usage || 1);
        let subRecipe = collectIngredients(ingredient, mainRecipeId);
        if (subRecipe) {
          flatRecipe.push({
            ...ingredient,
            items: subRecipe,
            type: "recipe",
            quantity: recipeSold[mainRecipeId] * (ingredient.usage ?? 0),
          });
        }
      } else {
        ingredientSoldQty[ingredient.id] =
          (ingredientSoldQty[ingredient.id] || 0) +
          // ingredient.usage * (recipeSold[mainRecipeId] || 0); // working
          (ingredient.usage ?? 0) * (recipe.usage || 1) * (recipeSold[mainRecipeId] || 0);
        // (ingredientSoldQty[ingredient.id] || 0) + (recipe.usage || 1) * ingredient.usage * (recipeSold[mainRecipeId] || 0);
        flatRecipe.push({
          ...ingredient,
          type: "ingredient",
          quantity:
            recipeSold[mainRecipeId] * (recipe.usage ?? 0)
              ? recipe.usage ?? 0 * (ingredient.usage ?? 0)
              : ingredient.usage,
        });
      }
    }
    return flatRecipe;
  }

  function calculateIngredientPricePerRecipe(recipe: RecipeFull) {
    if (recipe.items === undefined) {
      return;
    }
    Object.values(recipe.items).forEach((ingredient) => {
      if (!ingredient.id || !ingredient.quantity) {
        return;
      }
      if (ingredient.type === "recipe") {
        calculateIngredientPricePerRecipe(ingredient);
        ingredient.price_per_day =
          ingredient.items.reduce((acc, item) => acc + (item.price_per_day || 0), 0) *
          (ingredient.quantity / ingredientSoldQty[ingredient.id]);
        return;
      }
      let invoice_lines = connectedLinesManual.find(
        (line) => line.recipe_ingredient_item_name === ingredient.name
      )?.invoice_lines;
      if (!invoice_lines?.length) {
        ingredient.no_attached_invoice_lines = true;
        ingredient.price_per_day = 0;
        return;
      }

      allocatedInvoiceLines.push(...invoice_lines.map((line) => line.invoice_line_item_id));

      let aggregated = invoice_lines.reduce((acc: InvoiceLine, line: InvoiceLine) => {
        acc = {
          ...acc,
          min_date: acc.min_date
            ? acc.min_date < line.min_date
              ? acc.min_date
              : line.min_date
            : line.min_date,
          max_date: acc.max_date
            ? acc.max_date > line.max_date
              ? acc.max_date
              : line.max_date
            : line.max_date,
          total_price_cents:
            Number(acc.total_price_cents) +
            (line.min_date == line.max_date
              ? Number(line.total_price_cents_spent_ever)
              : Number(line.total_price_cents)),
          total_price_cents_spent_ever:
            Number(acc.total_price_cents_spent_ever) + Number(line.total_price_cents_spent_ever),
        };
        return acc;
      });

      let days_spent_for = Math.round(
        (Date.parse(aggregated.max_date) - Date.parse(aggregated.min_date)) / (1000 * 60 * 60 * 24)
      );

      if (days_spent_for == 0) {
        let max_date = startOfDay($priceHistoryDatePickerState.endDate);
        aggregated.max_date = `${max_date.getFullYear()}-${String(max_date.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(max_date.getDate()).padStart(2, "0")}T00:00:00.000Z`;

        aggregated.total_price_cents = aggregated.total_price_cents_spent_ever;
        days_spent_for = totalSalesDays;
      }

      if (!recipe.id) {
        throw new Error("Missing recipe id");
      }

      ingredient.days_spent_for = days_spent_for;
      ingredient.price_per_day =
        (Number(aggregated.total_price_cents) / ingredient.days_spent_for) * // price per day
          ((ingredient.quantity * recipeSold[recipe.id]) / ingredientSoldQty[ingredient.id]) || 0; //partial from the whole quantity sold
    });
    return recipe;
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  }

  async function getInvoiceLines() {
    gettingInvoiceLines = true;
    try {
      const body = {
        buyer_id: buyer?.id,
      };
      const result = await api
        .post("invoice-lines", { body: JSON.stringify(body) })
        .json<InvoiceLine[]>();
      allInvoiceLines = result;
      gettingInvoiceLines = false;
    } catch (e) {
      gettingInvoiceLines = false;
      console.error(e);
    }
  }

  async function handlePaste(event: ClipboardEvent) {
    if (steps[currentStep].component !== "recipes" || !waitingForSalesDataPaste) return;

    waitingForSalesDataPaste = false;

    try {
      const pastedText = event?.clipboardData?.getData("text");
      // console.log("Pasted text:", pastedText);

      linkingSalesData = true;

      const response = await api.post("link-sales-data", {
        body: JSON.stringify({
          buyer_id: buyer?.id,
          sales_data: pastedText,
          recipe_ids: selectedRecipeIds,
        }),
      });

      if (response.status === 200) {
        const linkedSales = (await response.json()) as $FixMe[];

        for (const linkedSale of Array.from(linkedSales)) {
          recipeSold[linkedSale.recipe_id] = linkedSale.quantity;
        }
      } else {
        console.error("API request failed with status code:", response.status);
      }

      linkingSalesData = false;
    } catch (e) {
      console.error(e);
    }
  }

  onMount(() => {
    document.addEventListener("paste", handlePaste);

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  });

  $: if (recipes) {
    selectedRecipeIds = recipes.flatMap((recipe) => recipe.id ?? []);
  } else {
    selectedRecipeIds = [];
  }

  $: isCheckedRecipe = (recipeId: string) => {
    return recipeSold[recipeId] > 0;
  };

  let selectedRecipeIdsString = JSON.stringify(selectedRecipeIds);

  $: {
    if (JSON.stringify(selectedRecipeIds) !== selectedRecipeIdsString) {
      allInvoiceLines = [];
      selectedRecipeIdsString = JSON.stringify(selectedRecipeIds);
    }
  }

  const toggleCheckboxRecipe = (recipeId: string) => {
    if (isCheckedRecipe(recipeId)) {
      selectedRecipeIds = selectedRecipeIds.filter((id) => id !== recipeId);
      recipeSold[recipeId] = 0;
    } else {
      recipeSold[recipeId] = 1;
    }
  };

  const fetchConnectedLines = async () => {
    let allConnectedLines: RecipeLineMatch[] = [];
    if (linkingRecipesToInvoices) {
      return;
    }

    if (connectedLines.length > 0) {
      connectedLines = [];
    }
    let matchingInvoiceLines = [];
    collectIngredientsForAllRecipes();

    matchingInvoiceLines = [...databaseConnectedLines];
    for (const ingredientId in ingredientSoldQty) {
      let ingredient = searchIndex.get(ingredientId);
      let invoiceLines = matchingInvoiceLines.find(
        (line: $FixMe) => line.recipe_ingredient_item_name === ingredient?.name
      )?.invoice_lines;

      let fullInvoiceLines: InvoiceLine[] = [];
      if (invoiceLines) {
        for (const line of invoiceLines) {
          let fullLine = allInvoiceLines.find(
            (fullLine) => fullLine.invoice_line_item_name === line
          );
          if (fullLine) {
            fullInvoiceLines.push(fullLine);
          }
        }
      }

      allConnectedLines.push(<RecipeLineMatch>{
        recipe_ingredient_item_name: ingredient?.name ?? "Invariant",
        recipe_ingredient_item_id: ingredient?.id ?? "Invariant",
        invoice_lines: fullInvoiceLines,
      });
    }
    connectedLines = allConnectedLines;
  };

  function collectIngredientsForAllRecipes(): Record<string, RecipeFull> {
    ingredientSoldQty = {};
    let flatIngredients: Record<string, RecipeFull> = {};
    for (const recipe of recipes) {
      if (!recipe.id) continue;
      if (!flatIngredients[recipe.id]) {
        flatIngredients[recipe.id] = {
          ...recipe,
          items: [],
          type: "recipe",
        };
      }
      flatIngredients[recipe.id] = {
        ...recipe,
        items: collectIngredients(recipe, recipe.id),
      };
    }
    return flatIngredients;
  }

  function collectUnallocatedInvoiceLines(): void {
    unallocatedInvoiceLines = allInvoiceLines.filter(
      (line) =>
        !allocatedInvoiceLines.includes(line.invoice_line_item_id) &&
        line.total_price_cents &&
        line.total_price_cents > 0
    );
    unallocatedInvoiceLines = unallocatedInvoiceLines.sort(
      (a, b) => (b.total_price_cents || 0) - (a.total_price_cents || 0)
    );
  }

  const calculate = async () => {
    recipeFlatIngredients = collectIngredientsForAllRecipes();
    await getInvoiceLines();
    await fetchConnectedLines();

    for (const recipe in recipeFlatIngredients) {
      const res = calculateIngredientPricePerRecipe(recipeFlatIngredients[recipe]);
      if (!res) continue;
      recipeFlatIngredients = {
        ...recipeFlatIngredients,
        recipe: res,
      };
    }

    collectUnallocatedInvoiceLines();
  };

  function toggleCheckboxInvoice(_id: string): any {
    throw new Error("Function not implemented.");
  }

  function isCheckedInvoice(_id: string): boolean | null | undefined {
    throw new Error("Function not implemented.");
  }

  $: {
    if (JSON.stringify(recipeSold) !== JSON.stringify({})) {
      recipeFlatIngredients = collectIngredientsForAllRecipes();
    }
  }

  // $: {
  //   if (JSON.stringify(databaseConnectedLines) !== JSON.stringify([]) && isOpen) {
  //     calculate();
  //   }
  // }
  $: connectedLinesManual = JSON.parse(JSON.stringify(connectedLines));

  $: if (
    steps[currentStep].component === "calculate" &&
    JSON.stringify(databaseConnectedLines) !== JSON.stringify([]) &&
    isOpen
  ) {
    calculate();
  }

  $: totalSalesDays = Math.round(
    (+$priceHistoryDatePickerState.endDate - +$priceHistoryDatePickerState.startDate) /
      (1000 * 60 * 60 * 24) +
      1
  );
</script>

{#if isOpen}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <div
    class="modal"
    on:click={(e) => {
      if (e.target === e.currentTarget) {
        isOpen = false;
      }
    }}
  >
    <div class="modal-content">
      <div class="modal-header">
        <h1>recipe costing</h1>
        <button on:click={() => (isOpen = false)}>&times;</button>
      </div>
      <div class="modal-body">
        <div class="recipes-to-email-notification">
          <div class="recipes-to-email-notification-icon">
            <svg
              height="64px"
              width="64px"
              version="1.1"
              id="_x32_"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              viewBox="0 0 512 512"
              xml:space="preserve"
              fill="#000000"
              ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                id="SVGRepo_tracerCarrier"
                stroke-linecap="round"
                stroke-linejoin="round"
              /><g id="SVGRepo_iconCarrier">
                <style type="text/css">
                  .st0 {
                    fill: #000000;
                  }
                </style>
                <g>
                  <path
                    class="st0"
                    d="M510.678,112.275c-2.308-11.626-7.463-22.265-14.662-31.054c-1.518-1.915-3.104-3.63-4.823-5.345 c-12.755-12.818-30.657-20.814-50.214-20.814H71.021c-19.557,0-37.395,7.996-50.21,20.814c-1.715,1.715-3.301,3.43-4.823,5.345 C8.785,90.009,3.63,100.649,1.386,112.275C0.464,116.762,0,121.399,0,126.087V385.92c0,9.968,2.114,19.55,5.884,28.203 c3.497,8.26,8.653,15.734,14.926,22.001c1.59,1.586,3.169,3.044,4.892,4.494c12.286,10.175,28.145,16.32,45.319,16.32h369.958 c17.18,0,33.108-6.145,45.323-16.384c1.718-1.386,3.305-2.844,4.891-4.43c6.27-6.267,11.425-13.741,14.994-22.001v-0.064 c3.769-8.653,5.812-18.171,5.812-28.138V126.087C512,121.399,511.543,116.762,510.678,112.275z M46.509,101.571 c6.345-6.338,14.866-10.175,24.512-10.175h369.958c9.646,0,18.242,3.837,24.512,10.175c1.122,1.129,2.179,2.387,3.112,3.637 L274.696,274.203c-5.348,4.687-11.954,7.002-18.696,7.002c-6.674,0-13.276-2.315-18.695-7.002L43.472,105.136 C44.33,103.886,45.387,102.7,46.509,101.571z M36.334,385.92V142.735L176.658,265.15L36.405,387.435 C36.334,386.971,36.334,386.449,36.334,385.92z M440.979,420.597H71.021c-6.281,0-12.158-1.651-17.174-4.552l147.978-128.959 l13.815,12.018c11.561,10.046,26.028,15.134,40.36,15.134c14.406,0,28.872-5.088,40.432-15.134l13.808-12.018l147.92,128.959 C453.137,418.946,447.26,420.597,440.979,420.597z M475.666,385.92c0,0.529,0,1.051-0.068,1.515L335.346,265.221L475.666,142.8 V385.92z"
                  />
                </g>
              </g></svg
            >
          </div>
          <div>
            Be sure to send all your invoices from the past three months to
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <div
              class="copy-email"
              class:success={emailCopySuccess}
              on:click={() =>
                emailCopy(
                  `${buyer?.id}@insights.rekki.com`
                  // `${buyer?.name.toLowerCase().replace(/ /g, "-")}@insights.rekki.com`
                )}
            >
              {buyer?.id}@insights.rekki.com
              <!-- {buyer?.name.toLowerCase().replace(/ /g, "-")}@insights.rekki.com -->
            </div>
            to ensure accurate recipe costing.
          </div>
        </div>
        <div class="accordion">
          {#each steps as details, step}
            <div class="accordion-step" class:open={step == currentStep}>
              <header>
                <h2>
                  {details.title}
                </h2>
                {#if step < currentStep}
                  <ButtonSmall color="neutral-02" size="tiny" on:click={() => (currentStep = step)}
                    >Edit</ButtonSmall
                  >
                {:else if step === currentStep && step < steps.length - 1}{" "}{/if}
              </header>
              <div class="accordion-body" class:closed={step != currentStep}>
                {#if details.component === "invoices" && invoices && details.component === steps[currentStep].component}
                  {#each invoices as invoice (invoice.id)}
                    <!-- svelte-ignore a11y-click-events-have-key-events -->
                    <div class="invoice-item" on:click={() => toggleCheckboxInvoice(invoice.id)}>
                      <input type="checkbox" checked={isCheckedInvoice(invoice.id)} />
                      <div class="invoice-date">{formatDate(invoice.invoiceDate)}</div>
                      <div class="invoice-supplier">{invoice.supplier}</div>
                      <div class="invoice-amount">
                        {(invoice.totalAmountCents / 100).toFixed(2)}
                      </div>
                    </div>
                  {/each}
                {/if}
                {#if details.component === "recipes" && recipes && details.component === steps[currentStep].component}
                  <Text size="small" color="neutral-03">{details.description}</Text>
                  <div class="flex-row">
                    <DatePicker
                      content={priceHistoryDatePickerState.datePickerContent}
                      startDate={$priceHistoryDatePickerState.startDate}
                      endDate={$priceHistoryDatePickerState.endDate}
                      pickerState={$priceHistoryDatePickerState.state}
                      onDateSelect={(date) => {
                        priceHistoryDatePickerState.dispatch({
                          type: "dateSelected",
                          payload: date,
                        });
                      }}
                      onOpen={() => {
                        priceHistoryDatePickerState.dispatch({
                          type: "open",
                        });
                      }}
                      onCancel={() => {
                        priceHistoryDatePickerState.dispatch({
                          type: "cancel",
                        });
                      }}
                      onChange={(startDate, endDate) => {
                        priceHistoryDatePickerState.dispatch({
                          type: "change",
                          payload: {
                            startDate,
                            endDate,
                          },
                        });
                      }}
                      onConfirm={() => {
                        priceHistoryDatePickerState.dispatch({
                          type: "confirm",
                        });
                      }}
                    />
                    <!-- svelte-ignore a11y-click-events-have-key-events a11y-no-noninteractive-tabindex -->
                    <div
                      class="link-sales-data {linkingSalesData ? '' : 'cursor-oversized'}"
                      class:gpt={linkingSalesData}
                      class:active={waitingForSalesDataPaste}
                      tabindex="0"
                      on:click={() => {
                        waitingForSalesDataPaste = true;
                      }}
                      on:blur={() => {
                        waitingForSalesDataPaste = false;
                      }}
                    >
                      click here and paste sales report contents to get automatically linked sales
                      data
                    </div>
                  </div>
                  {#each recipes as recipe (recipe.id)}
                    <div class="recipe-item">
                      <input
                        type="checkbox"
                        checked={isCheckedRecipe(recipe.id)}
                        on:change={() => toggleCheckboxRecipe(recipe.id)}
                      />
                      <!-- svelte-ignore a11y-click-events-have-key-events -->
                      <div class="recipe-name" on:click={() => toggleCheckboxRecipe(recipe.id)}>
                        {recipe.name}
                        {#if percentIngredientsLinkedForRecipe[recipe.id] < 1}
                          <div class="warning-pill">
                            <div class="warning">
                              <svg
                                viewBox="0 0 24 24"
                                width="16"
                                height="16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                ><g id="SVGRepo_bgCarrier" stroke-width="0" /><g
                                  id="SVGRepo_tracerCarrier"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                /><g id="SVGRepo_iconCarrier">
                                  <path
                                    d="M12 5L12 14"
                                    stroke="#000000"
                                    stroke-width="3"
                                    stroke-linecap="round"
                                    stroke-linejoin="round"
                                  />
                                  <path
                                    d="M13.8417 18.8417C13.8417 19.8588 13.0171 20.6833 12 20.6833C10.9829 20.6833 10.1583 19.8588 10.1583 18.8417C10.1583 17.8245 10.9829 17 12 17C13.0171 17 13.8417 17.8245 13.8417 18.8417Z"
                                    fill="#000000"
                                  />
                                </g></svg
                              >
                            </div>
                            MISSING LINKS, CALCULATIONS MIGHT BE INACCURATE
                          </div>
                        {/if}
                      </div>
                      <input
                        type="number"
                        class="recipe-quantity"
                        bind:value={recipeSold[recipe.id]}
                        placeholder="0"
                        min={0}
                        size={5}
                      />
                    </div>
                  {/each}
                  <div class="calculate-button-container">
                    <ButtonSmall on:click={nextStep}>Calculate GP</ButtonSmall>
                  </div>
                {/if}
                {#if details.component === "connect" && details.component === steps[currentStep].component}
                  <div class="recipe-ingredient-invoice-line-links">
                    {#if linkingRecipesToInvoices}
                      <div class="link-sales-data" class:gpt={linkingRecipesToInvoices}>
                        matching ingredients to invoice lines. might take a minute or 15
                      </div>
                    {/if}
                    {#each connectedLines as line, index (line.recipe_ingredient_item_id)}
                      <RecipeIngredientLink
                        connectedLines={connectedLinesManual}
                        {index}
                        {allInvoiceLines}
                        on:updateConnectedLines={(e) => {
                          connectedLinesManual = e.detail;
                        }}
                      />
                    {/each}
                  </div>
                {/if}
                {#if details.component === "calculate" && details.component === steps[currentStep].component}
                  {#if !connectedLines?.length || gettingInvoiceLines}
                    <LoadingSpinner />
                  {:else}
                    <div class="recipe-prices">
                      <table style="width: 100%;">
                        <thead>
                          <tr>
                            <th class="calculation-header-cell" style="text-align: left;">
                              recipe name
                            </th>
                            <th class="calculation-header-cell" style="text-align: right;">
                              cost per dish
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {#each recipes.filter((r) => r.id) as recipe}
                            {#if recipe.items && selectedRecipeIds.includes(recipe.id) && recipeSold[recipe.id] > 0}
                              <RecipeCostingPriceLine
                                recipe={recipeFlatIngredients[recipe.id]}
                                {connectedLinesManual}
                                {ingredientSoldQty}
                                {recipeSold}
                                {totalSalesDays}
                                {allInvoiceLines}
                                {ingredientPriceOverrides}
                                on:ingredientPriceOverride={(e) => {
                                  ingredientPriceOverrideSet(e.detail.ingredientId, e.detail.price);
                                }}
                              />
                            {/if}
                          {/each}
                          {#if unallocatedInvoiceLines.length > 0}
                            <tr class="recipe-row">
                              <td>unallocated bucket</td>
                              <td class="text-ocd" style="text-align: right;">
                                {(
                                  unallocatedInvoiceLines.reduce(
                                    (acc, line) =>
                                      allocatedInvoiceLines.includes(line.invoice_line_item_id)
                                        ? acc
                                        : acc + (line.total_price_cents || 0),
                                    0
                                  ) / 100
                                ).toFixed(2)}
                              </td>
                            </tr>
                            {#each unallocatedInvoiceLines as invoiceLine}
                              <tr class="ingredient-row">
                                <td style="padding-left: 10px;">
                                  {invoiceLine.invoice_line_item_name}
                                </td>
                                <td class="text-ocd" style="text-align: right;">
                                  {((invoiceLine.total_price_cents || 0) / 100).toFixed(2)}
                                </td>
                              </tr>
                            {/each}
                          {/if}
                        </tbody>
                      </table>
                    </div>
                  {/if}
                {/if}
              </div>
            </div>
          {/each}
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    justify-content: flex-end;
    z-index: 9999;
    padding: 20px;
    box-sizing: border-box;
  }

  .modal-content {
    width: 50%;
    background-color: var(--rekki-neutral-01);
    padding: 24px 32px;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
  }

  .modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
  }

  .modal-header h1 {
    margin: 0;
  }

  .modal-header button {
    background-color: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
  }

  .modal-body {
    margin-bottom: 20px;
    overflow: auto;
    flex: 1;
  }

  .accordion {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .accordion-step {
    background-color: #fff;
    border-radius: 16px;
  }
  .accordion-body {
    padding: 16px 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .accordion-body.closed {
    display: none;
  }

  .link-sales-data {
    padding: 10px 16px;
    border-radius: 4px;
    font-size: 13px;
    text-align: center;
    font-weight: 500;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 2px dashed var(--rekki-neutral-01);
    border-radius: 8px;
    color: var(--rekki-neutral-02);
    line-height: 1.2;
  }
  .link-sales-data.active {
    color: transparent;
  }
  .link-sales-data.gpt {
    animation: pulse 2s infinite;
    color: transparent;
  }
  .link-sales-data.active::before {
    position: absolute;
    width: 100%;
    text-align: center;
    content: "paste your data now please";
    color: #000;
  }
  .link-sales-data.gpt::before {
    position: absolute;
    width: 100%;
    text-align: center;
    content: "linking sales data...";
    color: #000;
  }
  @keyframes pulse {
    0% {
      opacity: 0.2;
    }
    80% {
      opacity: 1;
    }
    100% {
      opacity: 0.2;
    }
  }
  .accordion-step header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 20px;
  }
  .accordion-step.open header {
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }
  .accordion-step header:last-child {
    border-bottom: none;
  }

  /* .accordion-step header button {
    background-color: transparent;
    border: none;
    font-size: 1rem;
    cursor: pointer;
  } */

  .invoice-item {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0;
  }
  .recipe-row {
    font-size: 120%;
  }
  .recipe-row td {
    padding-top: 10px;
  }
  .recipe-item {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 10px 0;
    font-size: 18px;
  }

  .recipe-item + .recipe-item {
    border-top: 1px solid var(--rk-divider-1);
  }

  .recipe-name {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  .recipe-quantity {
    font-size: 18px;
    text-align: right;
    border-radius: 8px;
    background-color: var(--rekki-neutral-01);
    padding: 18px 16px;
    max-width: 120px;
    appearance: none;
  }

  .recipe-ingredient-invoice-line-links {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .ingredient-row {
    opacity: 0.6;
  }

  .calculation-header-cell {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  }

  .recipes-to-email-notification {
    padding: 12px;
    border-radius: 8px;
    padding: 20px;
    /* max-width: 510px; */
    width: min-content;
    margin: 0 0 20px;
    background-color: var(--rekki-light);
    display: flex;
    gap: 20px;
    align-items: center;
  }

  .recipes-to-email-notification svg {
    opacity: 0.1;
  }

  .recipes-to-email-notification-icon {
    line-height: 0;
  }

  .copy-email {
    padding: 2px 4px;
    border-radius: 4px;
    background-color: var(--rekki-neutral-01);
    /* display: inline-block; */
    white-space: nowrap;
    cursor: pointer;
    position: relative;
  }

  .copy-email.success {
    color: transparent;
  }

  .copy-email.success::before {
    position: absolute;
    width: 100%;
    text-align: center;
    content: "email copied to clipboard";
    color: #000;
  }
  .calculate-button-container {
    position: sticky;
    bottom: 0;
    background: linear-gradient(180deg, #fff0 0%, #fff 95%);
    margin: 0 -20px;
    padding: 20px;
    display: flex;
    justify-content: center;
  }
  .flex-row {
    display: flex;
    gap: 10px;
  }
  :global(.title-container) {
    border-radius: 8px !important;
  }

  .warning {
    font-size: 12px;
    font-weight: bold;
    background-color: var(--rekki-warning);
    width: 16px;
    height: 16px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
  }
  .warning-pill {
    display: flex;
    gap: 4px;
    font-family: "REKKI-OCD";
    font-size: 12px;
    align-items: center;
  }
</style>
